import {Avatar, Typography} from '@sollapay/ui';

import {getInitials} from '@/utils';

import type {FC, ReactNode} from 'react';

export interface PersonCellProps {
  /** Rendered next to the avatar. Falls back to `emptyLabel` when null. */
  name: string | null;
  /**
   * Initials source, if different from `name` — e.g. a "you" label shouldn't source
   * initials from itself. Omit to derive initials from `name`; pass `null` explicitly
   * to force a blank avatar (the real name exists but hasn't resolved).
   */
  initialsSource?: string | null;
  /** Rendered on the name line, after the name, e.g. a "you" badge. */
  nameEnd?: ReactNode;
  subtitle?: string;
  /** Rendered under the name/subtitle, e.g. a team stack. */
  footer?: ReactNode;
  avatarUrl?: string;
  /** Stable seed for deterministic avatar colour (e.g. the person's id). */
  seed?: string;
  emptyLabel: string;
  size?: 'xs' | 'sm' | 'default' | 'lg' | 'xl';
}

/** Avatar + name (+ optional subtitle) cell, shared by uploader and trustee attribution displays. */
export const PersonCell: FC<PersonCellProps> = ({
  name,
  initialsSource,
  nameEnd,
  subtitle,
  footer,
  avatarUrl,
  seed,
  emptyLabel,
  size
}) => {
  const initialsInput = initialsSource !== undefined ? initialsSource : name;
  const initials = initialsInput ? getInitials(initialsInput) : '';

  return (
    <div className="flex items-center gap-3">
      <Avatar initials={initials} src={avatarUrl} seed={seed} size={size} noShadow />
      <div className="flex flex-col gap-0.5 min-w-0">
        <div className="flex items-center gap-2">
          <Typography size="sm" weight="medium" className="min-w-0 truncate">
            {name ?? emptyLabel}
          </Typography>
          {nameEnd}
        </div>
        {subtitle && (
          <Typography size="xs" color="tertiary">
            {subtitle}
          </Typography>
        )}
        {footer}
      </div>
    </div>
  );
};
